/**
 * The left panel: two tabs, because it was doing two jobs in one scroll.
 *
 * "Add a table" is used in bursts at the start and then almost never; the guest
 * roster is used constantly and wants the whole column. Stacked, the roster was
 * permanently pushed below the fold by a palette nobody was looking at. Split,
 * each gets the panel to itself.
 *
 * The Guests tab shows everyone, grouped: unseated first, because "who still
 * needs a chair?" is the question the tab exists to answer.
 */

import { AnimatePresence, motion } from 'framer-motion'
import { useMemo, useRef, useState } from 'react'

import { TOUCH_FIRST, useMediaQuery } from '../hooks/useMediaQuery'
import { avatarTint, initialsOf } from '../lib/colors'
import { FEATURES } from '../lib/config'
import { beginGuestDrag } from '../lib/dragGuest'
import { maxSeatCount } from '../lib/geometry'
import { type RsvpFilter, matchesRsvp, rsvpTally, waitsForChair } from '../lib/guests'
import type { Catalogue } from '../lib/i18n'
import { useT } from '../lib/i18n/I18nProvider'
import { railShape } from '../lib/i18n/shapeName'
import type { Guest, Plan, TableShape } from '../lib/types'
import { guestSeatIndex, usePlanStore } from '../store/planStore'
import { screenToWorld, useViewStore } from '../store/viewStore'

interface Props {
  plan: Plan
  stageRef: React.RefObject<HTMLDivElement>
  onImportFloorPlan: () => void
}

/** Named at render through `railShape`: one shape is a head table outside weddings. */
const TABLE_KINDS: Array<{ shape: TableShape; icon: JSX.Element }> = [
  { shape: 'round', icon: <span className="shape-chip shape-chip--round" /> },
  { shape: 'square', icon: <span className="shape-chip shape-chip--square" /> },
  { shape: 'rectangle', icon: <span className="shape-chip shape-chip--rectangle" /> },
  { shape: 'sweetheart', icon: <span className="shape-chip shape-chip--sweetheart" /> },
]

export function LeftRail({ plan, stageRef, onImportFloorPlan }: Props) {
  const t = useT()
  const [tab, setTab] = useState<'tables' | 'guests'>('tables')

  const seatIndex = useMemo(() => guestSeatIndex(plan), [plan])
  const seatedTotal = plan.guests.filter((guest) => seatIndex.has(guest.id)).length

  return (
    <aside className="rail">
      <div className="rail-tabs" role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'tables'}
          className={`rail-tab${tab === 'tables' ? ' is-active' : ''}`}
          onClick={() => setTab('tables')}
        >
          {t.rail.tabTables}
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'guests'}
          className={`rail-tab${tab === 'guests' ? ' is-active' : ''}`}
          onClick={() => setTab('guests')}
        >
          {t.rail.tabGuests}
          {plan.guests.length > 0 && <span className="mono rail-tab-count">{plan.guests.length}</span>}
        </button>
      </div>

      {tab === 'tables' ? (
        <TablesTab plan={plan} stageRef={stageRef} onImportFloorPlan={onImportFloorPlan} />
      ) : (
        <GuestsTab plan={plan} seatIndex={seatIndex} seatedTotal={seatedTotal} />
      )}
    </aside>
  )
}

// --- tables ------------------------------------------------------------------

/** Also the compact editor's Tables sheet. */
export function TablesTab({ plan, stageRef, onImportFloorPlan }: Props) {
  const t = useT()
  const touch = useMediaQuery(TOUCH_FIRST)
  const addTable = usePlanStore((s) => s.addTable)

  /** Drop new tables at the centre of what the planner is currently looking at. */
  const addTableAtViewCenter = (shape: TableShape) => {
    const rect = stageRef.current?.getBoundingClientRect()
    // Nudge off-centre so a second table doesn't land exactly on the first.
    const jitter = plan.tables.length % 2 === 0 ? 0 : 40
    const view = useViewStore.getState()
    const center = rect ? screenToWorld(view, rect.width / 2, rect.height / 2) : { x: 0, y: 0 }
    addTable(shape, { x: Math.round(center.x) + jitter, y: Math.round(center.y) + jitter })
    // In the sheet, get out of the way: the new table is selected, and the next
    // thing the planner wants is to see it land and drag it into place.
    if (view.compact) view.closePanel()
  }

  return (
    <div className="rail-body scroll-y">
      <span className="eyebrow rail-eyebrow">{touch ? t.rail.tapToAdd : t.rail.dragOntoRoom}</span>

      <div className="shape-list">
        {TABLE_KINDS.map((kind) => {
          const words = railShape(t, kind.shape, plan.eventKind)
          return (
            <button
              key={kind.shape}
              type="button"
              className="shape-card"
              onClick={() => addTableAtViewCenter(kind.shape)}
            >
              {kind.icon}
              <span className="shape-text">
                <strong>{words.title}</strong>
                <small>
                  {/* The cap is per shape: a round table stops at 13, only the
                      banquet reaches 20. Promising the global ceiling here sold
                      chairs `addSeat` would then refuse. */}
                  {words.blurb ??
                    t.rail.chairsBlurb(plan.settings.defaultSeats[kind.shape], maxSeatCount(kind.shape))}
                </small>
              </span>
            </button>
          )
        })}
      </div>

      {/* Most planners are copying a room the venue already designed. Offering
          that here, beside the shapes, is the difference between an hour of
          dragging and a photo. */}
      {FEATURES.floorPlanImport && (
        <>
          <span className="eyebrow rail-eyebrow rail-eyebrow--spaced">{t.rail.orFromPaper}</span>
          <button type="button" className="shape-card shape-card--dashed" onClick={onImportFloorPlan}>
            <span className="shape-chip shape-chip--photo">
              <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
                <path
                  d="M4 7h3l1.5-2h7L17 7h3v12H4V7z"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinejoin="round"
                />
                <circle cx="12" cy="13" r="3.2" fill="none" stroke="currentColor" strokeWidth="1.5" />
              </svg>
            </span>
            <span className="shape-text">
              <strong>{t.rail.fromPhoto}</strong>
              <small>{t.rail.fromPhotoBlurb}</small>
            </span>
          </button>
        </>
      )}
    </div>
  )
}

// --- guests ------------------------------------------------------------------

/**
 * The chips over the roster, in the order an answer travels: everybody, then
 * the ones who are coming, the ones who have not said, and the ones who can't.
 * They borrow the inspector's words for the three answers, so a guest's panel
 * and the list never name the same thing differently.
 */
const RSVP_CHIPS: ReadonlyArray<{ value: RsvpFilter; label: (t: Catalogue) => string }> = [
  { value: 'all', label: (t) => t.rail.rsvpAll },
  { value: 'yes', label: (t) => t.inspector.rsvpComing },
  { value: 'pending', label: (t) => t.inspector.rsvpPending },
  { value: 'no', label: (t) => t.inspector.rsvpCant },
]

/** Also the compact editor's Guests sheet. */
export function GuestsTab({
  plan,
  seatIndex,
  seatedTotal,
}: {
  plan: Plan
  seatIndex: ReturnType<typeof guestSeatIndex>
  seatedTotal: number
}) {
  const t = useT()
  const touch = useMediaQuery(TOUCH_FIRST)
  const [query, setQuery] = useState('')
  const [filter, setFilter] = useState<RsvpFilter>('all')
  const [importing, setImporting] = useState(false)
  const [importText, setImportText] = useState('')
  const newGuestRef = useRef<HTMLInputElement>(null)

  const addGuest = usePlanStore((s) => s.addGuest)
  const importGuests = usePlanStore((s) => s.importGuests)
  const autoSeat = usePlanStore((s) => s.autoSeat)
  const showToast = usePlanStore((s) => s.showToast)

  const tally = useMemo(() => rsvpTally(plan.guests), [plan.guests])

  const { unseated, seated } = useMemo(() => {
    const needle = query.trim().toLowerCase()
    const matches = (guest: Guest) =>
      !needle ||
      guest.fullName.toLowerCase().includes(needle) ||
      (guest.groupLabel ?? '').toLowerCase().includes(needle)

    const all = plan.guests.filter(matches)
    return {
      // A decline is not waiting for a chair, so it stays out of the top list
      // unless the planner has asked for the declines by name.
      unseated: all.filter((guest) => !seatIndex.has(guest.id) && waitsForChair(guest, filter)),
      seated: all.filter((guest) => seatIndex.has(guest.id) && matchesRsvp(guest, filter)),
    }
  }, [plan.guests, query, filter, seatIndex])

  const submitGuest = (event: React.FormEvent) => {
    event.preventDefault()
    const input = newGuestRef.current
    const value = input?.value.trim()
    if (!value) return
    addGuest(value)
    if (input) input.value = ''
  }

  const runImport = () => {
    const added = importGuests(importText)
    setImportText('')
    setImporting(false)
    if (added > 0) showToast(t.rail.addedGuests(added), 'success')
  }

  const runAutoSeat = () => {
    const placed = autoSeat()
    showToast(
      placed > 0 ? t.rail.autoSeated(placed) : t.rail.everyoneHasChair,
      placed > 0 ? 'success' : 'info',
    )
  }

  const startGuestDrag = (guest: Guest) => (event: React.PointerEvent) => {
    // Keeps a mouse from selecting text on its way out of the list. Never for a
    // finger: WebKit can take that as cancelling the tap that seats someone.
    if (event.pointerType === 'mouse') event.preventDefault()
    const placement = seatIndex.get(guest.id)
    beginGuestDrag(
      event,
      guest.id,
      placement ? { tableId: placement.table.id, seatId: placement.seat.id } : null,
    )
  }

  // Only ever claimed about the whole roster: a filtered or searched list that
  // happens to be empty says nothing about the room.
  const expected = tally.all - tally.no
  const everyoneSeated =
    expected > 0 && unseated.length === 0 && !query.trim() && filter === 'all'

  return (
    <div className="rail-guests">
      <div className="rail-guest-head">
        <form onSubmit={submitGuest} className="guest-add">
          <input
            ref={newGuestRef}
            className="field"
            placeholder={t.rail.addGuestPlaceholder}
            aria-label={t.rail.guestName}
            maxLength={120}
          />
          <button type="submit" className="btn btn-primary" aria-label={t.rail.addGuest}>
            {t.common.add}
          </button>
        </form>

        <div className="guest-tools">
          <button type="button" className="btn btn-link" onClick={() => setImporting((v) => !v)}>
            {t.rail.pasteList}
          </button>
          <button
            type="button"
            className="btn btn-link"
            onClick={runAutoSeat}
            disabled={plan.guests.length === 0}
          >
            {t.rail.autoSeat}
          </button>
        </div>

        <AnimatePresence initial={false}>
          {importing && (
            <motion.div
              className="guest-import"
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2, ease: [0.23, 1, 0.32, 1] }}
            >
              <textarea
                className="field"
                value={importText}
                onChange={(event) => setImportText(event.target.value)}
                placeholder={t.rail.bulkPlaceholder}
                rows={6}
              />
              <div className="guest-import-actions">
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  onClick={() => setImporting(false)}
                >
                  {t.common.cancel}
                </button>
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  onClick={runImport}
                  disabled={!importText.trim()}
                >
                  {t.rail.importList}
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {plan.guests.length > 0 && (
        <>
          <div
            className="rsvp-filter"
            role="group"
            aria-label={t.rail.filterByRsvp}
          >
            {RSVP_CHIPS.map(({ value, label }) => (
              <button
                key={value}
                type="button"
                className={`rsvp-chip${filter === value ? ' is-active' : ''}`}
                onClick={() => setFilter(value)}
                aria-pressed={filter === value}
              >
                {label(t)}
                <span className="rsvp-chip-count">{tally[value]}</span>
              </button>
            ))}
          </div>

          <div className="rail-guest-search">
            <input
              className="field"
              placeholder={t.rail.searchGuests}
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              aria-label={t.rail.searchGuestsLabel}
            />
          </div>
        </>
      )}

      <div className="rail-guest-list scroll-y">
        {everyoneSeated && (
          <p className="rail-banner">
            <span className="dot" />
            {t.rail.allSeated(expected)}
          </p>
        )}

        {!everyoneSeated && (
          <GuestGroup
            title={t.rail.needsSeat}
            hint={touch ? t.gestures.tapNameThenChair : t.rail.needsSeatHint}
            guests={unseated}
            onDragStart={startGuestDrag}
            emptyMessage={
              plan.guests.length === 0
                ? t.rail.noGuestsYet
                : filter === 'all'
                  ? t.rail.everyoneSeated
                  : t.rail.noneWithAnswer
            }
          />
        )}

        {seated.length > 0 && (
          <GuestGroup
            title={`${t.rail.seated} · ${seatedTotal}`}
            guests={seated}
            onDragStart={startGuestDrag}
            seatIndex={seatIndex}
          />
        )}
      </div>
    </div>
  )
}

interface GroupProps {
  title: string
  hint?: string
  guests: Guest[]
  onDragStart: (guest: Guest) => (event: React.PointerEvent) => void
  emptyMessage?: string
  seatIndex?: ReturnType<typeof guestSeatIndex>
}

function GuestGroup({ title, hint, guests, onDragStart, emptyMessage, seatIndex }: GroupProps) {
  const t = useT()
  const setActiveSeat = usePlanStore((s) => s.setActiveSeat)

  /**
   * A seated guest opens their chair. Anyone still waiting is picked up for a
   * tap-to-seat — the drag's partner, and on a phone the way it is done: a
   * finger on a scrolling list scrolls the list, it never lifts a name out.
   */
  const choose = (guest: Guest) => {
    const placement = seatIndex?.get(guest.id)
    const view = useViewStore.getState()
    if (placement) {
      setActiveSeat({ tableId: placement.table.id, seatId: placement.seat.id })
      if (view.compact) view.openPanel('selection')
    } else {
      view.startPlacing(guest.id)
    }
  }

  return (
    <div className="guest-group">
      <div className="guest-group-head">
        <span className="eyebrow">
          {title}
          {!seatIndex && guests.length > 0 && ` · ${guests.length}`}
        </span>
        {hint && guests.length > 0 && <span className="guest-group-hint">{hint}</span>}
      </div>

      {guests.length === 0 ? (
        <p className="guest-empty">{emptyMessage}</p>
      ) : (
        <ul className="guest-list">
          <AnimatePresence initial={false}>
            {guests.map((guest) => {
              const placement = seatIndex?.get(guest.id)
              return (
                <motion.li
                  key={guest.id}
                  layout
                  initial={{ opacity: 0, y: -6 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -6 }}
                  transition={{ type: 'spring', duration: 0.35, bounce: 0.1 }}
                  className={`guest-chip${guest.rsvp === 'no' ? ' is-declined' : ''}`}
                  onPointerDown={onDragStart(guest)}
                  onClick={() => choose(guest)}
                  title={
                    placement
                      ? `${guest.fullName} — ${t.rail.seatAt(placement.table.name, placement.seat.position + 1)}`
                      : t.rail.dragOntoChair(guest.fullName)
                  }
                >
                  <span className="guest-avatar" style={{ background: avatarTint(guest.id) }}>
                    {guest.photoUrl ? (
                      <img src={guest.photoUrl} alt="" draggable={false} />
                    ) : (
                      initialsOf(guest.fullName)
                    )}
                  </span>
                  <span className="guest-chip-text">
                    <strong>
                      {guest.fullName}
                      {/* Only worth a mark where it costs something: a chair
                          held by someone who has said they cannot come. */}
                      {placement && guest.rsvp === 'no' && (
                        <span
                          className="guest-declined-mark"
                          title={t.inspector.declinedButSeated}
                          aria-label={t.inspector.declinedButSeated}
                        />
                      )}
                    </strong>
                    {placement ? (
                      <small>
                        {t.rail.seatAt(placement.table.name, placement.seat.position + 1)}
                      </small>
                    ) : (
                      guest.groupLabel && <small>{guest.groupLabel}</small>
                    )}
                  </span>
                </motion.li>
              )
            })}
          </AnimatePresence>
        </ul>
      )}
    </div>
  )
}
